import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IImageTextTile } from "@faire/web-api--source/indigofair/data/IImageTextTile";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { Button } from "@faire/web/slate/Button";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Ref, type JSX } from "react";
import Slider from "react-slick";
import styled from "styled-components";

interface IProps {
  tiles: IImageTextTile[];
  sliderRef: Ref<Slider>;
  isSSR: boolean;
}

interface IImageWrapperProps {
  tile: IImageTextTile;
  isSSR: boolean;
}

const getBackgroundPosition = (image: IImage | undefined) => {
  if (image?.tags.some((i) => i === "low_crop")) {
    return "center 70%";
  } else if (image?.tags.some((i) => i === "high_crop")) {
    return "center 30%";
  } else {
    return "center 50%";
  }
};

const ImageWrapper: React.FC<IImageWrapperProps> = ({ tile, isSSR }) => (
  <Wrapper>
    <BannerImage
      $image={optimizeImageUrl(
        {
          url: tile.image?.url,
        },
        isSSR,
        { width: 497 }
      )}
      $backgroundPosition={getBackgroundPosition(tile.image)}
    />
    <ImageCaption tile={tile} />
  </Wrapper>
);

const ImageCaption: React.FC<Pick<IImageWrapperProps, "tile">> = ({ tile }) => {
  if (!tile.cta_text) {
    return null;
  }

  return (
    <BrandName>
      <Typography variant="labelSansRegular" color={Core.text.subdued}>
        <StrictLocalMsg
          id="9x1Apf"
          defaultMessage="Featured brand: <LinkFormatting>{brandCompanyName}</LinkFormatting>"
          description={{
            text: "Caption below image on eco-friendly category page banner. There is a link on the brandCompanyName which leads to the brand page.",
            img: "https://cdn.faire.com/fastly/ce1534a8638885d8b899bd8ad85485e38aba4e4633348a7a293e16d74438c0e1.png",
          }}
          values={{
            brandCompanyName: tile.cta_text,
            LinkFormatting: LinkFormatting(tile.cta_link),
          }}
        />
      </Typography>
    </BrandName>
  );
};

function LinkFormatting(linkTo: string | undefined) {
  return function (chunks: JSX.Element) {
    if (linkTo) {
      return (
        <Button variant="tertiary" linkTo={linkTo}>
          <Typography variant="labelSansRegular" color={Core.text.subdued}>
            {chunks}
          </Typography>
        </Button>
      );
    } else {
      return chunks;
    }
  };
}

export const EcoFriendlyC2PageImageSlider: React.FC<IProps> = ({
  tiles,
  sliderRef,
  isSSR,
}: IProps) => {
  return (
    <StyledSlider
      adaptiveHeight={true}
      arrows={false}
      autoplay
      autoplaySpeed={5000}
      infinite
      pauseOnHover
      ref={sliderRef}
      draggable={false}
    >
      {tiles.map((tile, index) => (
        <ImageWrapper
          key={tile.image?.url ?? index}
          tile={tile}
          isSSR={isSSR}
        />
      ))}
    </StyledSlider>
  );
};

const StyledSlider = styled(Slider)`
  width: 50%;

  > div {
    height: 100% !important;
  }

  ${responsiveMediaQueries.tabletAndBelow} {
    width: 100%;
    height: 272px;
    > div {
      height: 272px !important;
    }
  }

  ${responsiveMediaQueries.mobile} {
    overflow: hidden;
    height: 120px;
    > div {
      height: 120px !important;
    }
  }

  .slick-track {
    height: 100%;
  }

  .slick-track div {
    height: 100%;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow-y: visible;
`;

const BrandName = styled.div`
  width: 100%;
  height: 20px !important;
  text-align: right;
  margin-top: 4px;
  position: absolute;
  left: 0;
  user-select: text;
  -moz-user-select: text;
  -webkit-user-select: text;

  ${responsiveMediaQueries.tabletAndBelow} {
    height: 0px !important;
    display: none;
  }
`;

const BannerImage = styled.div<{
  $image?: string;
  $backgroundPosition?: string;
}>`
  flex-shrink: 0;
  width: 100%;
  height: calc(100% - ${getSpacing("3x")}) !important;
  background-image: url(${(props) => props.$image});
  background-size: cover;
  background-position: ${(props) => props.$backgroundPosition};

  ${responsiveMediaQueries.tabletAndBelow} {
    height: 100% !important;
  }
`;
